import React, { useState, useEffect, useCallback, useRef } from 'react';
import './PaymentEditableSegment.css';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { Snackbar, Alert } from '@mui/material';

// Constants
const SNACKBAR_DURATION = 3000;
// Image size restriction (in KB)
const MAX_IMAGE_SIZE_KB = 35;

const TEMPLATE_NAME = 'gardenwall-disconnect-template';

// Disconnect template styling
const DISCONNECT_COLOR = '#fee2e2'; // Very light red - most severe

const DEFAULT_CONTENT = {
  textContent: 'Temporary Disconnection',
  descriptionContent: 'Your service is temporarily disconnected due to non-payment.',
  buttonLabel: 'Reconnect Now',
  secondaryButtonLabel: 'View Invoice',
  tertiaryButtonLabel: 'Support',
  theme: 'disconnect',
  backgroundColor: DISCONNECT_COLOR,
  titleColor: '#1e293b',
  descriptionColor: '#475569',
  amountColor: '#1e293b',
};

const DEFAULT_BUTTON_STYLE = {
  backgroundColor: '#2f9e44',
  textColor: '#FFFFFF',
  cornerRadius: '23px',
};
const DEFAULT_SECONDARY_STYLE = {
  backgroundColor: '#FFFFFF',
  textColor: '#333333',
  cornerRadius: '23px',
};

const TemporaryDisconnectBase = () => {
  const { token } = useAuth();

  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [buttonStyles, setButtonStyles] = useState(DEFAULT_BUTTON_STYLE);
  const [buttonStyles2, setButtonStyles2] = useState(DEFAULT_SECONDARY_STYLE);
  const [buttonStyles3, setButtonStyles3] = useState(DEFAULT_SECONDARY_STYLE);

  const [editingSegment, setEditingSegment] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  });

  const [previewMode, setPreviewMode] = useState(false);

  // Image upload states
  const [image, setImage] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const imageRef = useRef();

  const showSnackbar = (message, severity = 'info') =>
    setSnackbar({ open: true, message, severity });
  const handleSnackbarClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Image upload functions
  const handleImageClick = () => {
    if (imageRef.current) {
      imageRef.current.click();
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // --- Check file type ---
   
const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/svg+xml'];
if (!validTypes.includes(file.type)) {
  showSnackbar('Invalid format! Please upload a PNG, JPG, GIF, or SVG image.', 'error');
  e.target.value = '';
  return;
}

      // --- Check file size ---
      const fileSizeKB = Math.round(file.size / 1024);
      if (fileSizeKB > MAX_IMAGE_SIZE_KB) {
        showSnackbar(
          `Image too large! Maximum allowed size is ${MAX_IMAGE_SIZE_KB}KB. Your image is ${fileSizeKB}KB.`,
          'error'
        );
        e.target.value = '';
        return;
      }
  
      // --- Valid image ---
      setImage(file);
      showSnackbar('Image loaded successfully.', 'success');
  
      // Preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  
    // Allow re-upload of same file
    e.target.value = '';
  };
  
  const fileToBase64 = (file, callback) => {
    if (file && file instanceof Blob) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => callback(reader.result);
      reader.onerror = (error) => {
        console.error('Error converting file to base64:', error);
      };
    } else {
      console.error('Provided file is not a Blob:', file);
    }
  };

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/templates/list`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${token}`,
          },
        }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await response.json();
    } catch (err) {
      console.error('Error fetching templates:', err);
      showSnackbar('Failed to fetch templates', 'error');
      return [];
    }
  }, [token]);

  // Generate preview HTML
  const generateTemplateHTML = () => {
    return `
      <div style="background:${content.backgroundColor};padding:20px;text-align:center;border-radius:8px;font-family:Arial,sans-serif;">
        
        ${imageUrl ? `
          <div style="margin-bottom:16px;">
            <img src="${imageUrl}" alt="Service Icon" style="max-width:100px;height:auto;display:block;margin:0 auto;" />
          </div>
        ` : ''}
  
        <p style="color:${content.titleColor};font-size:18px;font-weight:bold;margin:0 0 8px 0;">
          ${content.textContent}
        </p>
  
        <h2 style="color:${content.amountColor};font-size:24px;margin:0 0 12px 0;">
          $70.00
        </h2>
  
        <p style="color:${content.descriptionColor};font-size:14px;margin:0 0 20px 0;">
          ${content.descriptionContent}
        </p>
  
        <div style="margin-bottom:12px;">
          <a href="/reconnect"
            style="background:${buttonStyles.backgroundColor};color:${buttonStyles.textColor};
                   border-radius:${buttonStyles.cornerRadius};padding:10px 20px;
                   text-decoration:none;display:inline-block;font-weight:bold;">
            ${content.buttonLabel}
          </a>
        </div>
  
        <div>
          <a href="/invoice"
            style="background:${buttonStyles2.backgroundColor};color:${buttonStyles2.textColor};
                   border-radius:${buttonStyles2.cornerRadius};padding:10px 20px;
                   text-decoration:none;display:inline-block;margin-right:8px;font-weight:bold;">
            ${content.secondaryButtonLabel}
          </a>
  
          <a href="/support"
            style="background:${buttonStyles3.backgroundColor};color:${buttonStyles3.textColor};
                   border-radius:${buttonStyles3.cornerRadius};padding:10px 20px;
                   text-decoration:none;display:inline-block;font-weight:bold;">
            ${content.tertiaryButtonLabel}
          </a>
        </div>
  
      </div>
    `;
  };
  
  // Save template
  const saveTemplate = useCallback(async () => {
    if (isLoading) return;
    setIsLoading(true);

    try {
      const appendDataAndSend = async (imageData) => {
        const templateHTML = generateTemplateHTML();

        const templateData = {
          ...content,
          buttonStyles,
          buttonStyles2,
          buttonStyles3,
          image: imageData,
          templateHTML: templateHTML
        };

        const existing = templates.find((t) => t.template_name === TEMPLATE_NAME);
        const isUpdate = existing?.id;
        const url = isUpdate
          ? `${process.env.REACT_APP_BASE_URL}/templates/update/${existing.id}`
          : `${process.env.REACT_APP_BASE_URL}/templates/create`;

        const response = await axios({
          method: isUpdate ? 'PUT' : 'POST',
          url,
          data: {
            template_name: TEMPLATE_NAME,
            template: JSON.stringify(templateData),
          },
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${token}`,
          },
        });

        if (response.status === 200 || response.status === 201) {
          showSnackbar('Template saved successfully!', 'success');
          setFetchTrigger((p) => p + 1);
          setEditingSegment(null);
        }
      };

      if (image) {
        fileToBase64(image, async (base64Image) => {
          await appendDataAndSend(base64Image);
          setImage(null); // Clear image after saving
        });
      } else {
        await appendDataAndSend(imageUrl);
      }
    } catch (err) {
      console.error('Save error:', err);
      showSnackbar('Failed to save template', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [content, buttonStyles, buttonStyles2, buttonStyles3, templates, token, isLoading, image, imageUrl]);

  // Load templates list
  useEffect(() => {
    (async () => {
      const fetched = await fetchTemplates();
      setTemplates(fetched);
    })();
  }, [fetchTemplates, fetchTrigger]);

  // When templates change, load matching template or defaults
  useEffect(() => {
    if (!templates.length) {
      setContent(DEFAULT_CONTENT);
      setButtonStyles(DEFAULT_BUTTON_STYLE);
      setButtonStyles2(DEFAULT_SECONDARY_STYLE);
      setButtonStyles3(DEFAULT_SECONDARY_STYLE);
      setImageUrl(null);
      return;
    }

    const template = templates.find((tpl) => tpl.template_name === TEMPLATE_NAME);

    if (template) {
      try {
        const parsed = JSON.parse(JSON.parse(template.template));
        setContent({
          ...DEFAULT_CONTENT,
          ...parsed,
          backgroundColor: parsed.backgroundColor || DISCONNECT_COLOR,
        });
        setButtonStyles(parsed.buttonStyles || DEFAULT_BUTTON_STYLE);
        setButtonStyles2(parsed.buttonStyles2 || DEFAULT_SECONDARY_STYLE);
        setButtonStyles3(parsed.buttonStyles3 || DEFAULT_SECONDARY_STYLE);
        setImageUrl(parsed.image || null);
      } catch (err) {
        console.error('Parse error:', err);
        showSnackbar('Error loading template data', 'error');
        setContent(DEFAULT_CONTENT);
        setImageUrl(null);
      }
    } else {
      setContent(DEFAULT_CONTENT);
      setButtonStyles(DEFAULT_BUTTON_STYLE);
      setButtonStyles2(DEFAULT_SECONDARY_STYLE);
      setButtonStyles3(DEFAULT_SECONDARY_STYLE);
      setImageUrl(null);
    }
  }, [templates]);

  // handlers
  const handleEditClick = (segment) =>
    setEditingSegment(editingSegment === segment ? null : segment);

  const handleContentChange = (f, v) =>
    setContent((prev) => ({ ...prev, [f]: v }));

  const handleButtonStyleChange = (buttonType, field, value) => {
    let setter;
    if (buttonType === 'primary') setter = setButtonStyles;
    else if (buttonType === 'secondary') setter = setButtonStyles2;
    else if (buttonType === 'tertiary') setter = setButtonStyles3;
    setter((prev) => ({ ...prev, [field]: value }));
  };

  // Button edit panel renderer
  function renderButtonEditModal(buttonType, styles) {
    const buttonTextValue = 
      buttonType === 'primary'
        ? content.buttonLabel
        : buttonType === 'secondary'
        ? content.secondaryButtonLabel
        : content.tertiaryButtonLabel;

    return (
      <div className="payment-button-edit-panel">
        <div className="payment-modal-header">
          <h3 className="payment-modal-title">
            Edit {buttonType === 'primary'
              ? 'Primary'
              : buttonType === 'secondary'
              ? 'View Invoice'
              : 'Contact Us'} Button
          </h3>
          <button
            className="payment-modal-close"
            onClick={() => setEditingSegment(null)}
          />
        </div>

        <div className="payment-edit-field">
          <label>Button Text:</label>
          <div className="input-wrapper">
            <input
              type="text"
              value={buttonTextValue}
              maxLength={25}
              onChange={(e) => {
                if (buttonType === 'primary') handleContentChange('buttonLabel', e.target.value);
                else if (buttonType === 'secondary') handleContentChange('secondaryButtonLabel', e.target.value);
                else handleContentChange('tertiaryButtonLabel', e.target.value);
              }}
              className="payment-edit-input"
            />
            <small className="char-counter">
              {buttonTextValue.length}/25
            </small>
          </div>
        </div>

        <div className="payment-edit-field">
          <label>BG Color:</label>
          <input
            type="color"
            value={styles.backgroundColor}
            onChange={(e) => handleButtonStyleChange(buttonType, 'backgroundColor', e.target.value)}
            className="payment-color-input"
          />
          <input
            type="text"
            value={styles.backgroundColor}
            onChange={(e) => handleButtonStyleChange(buttonType, 'backgroundColor', e.target.value)}
            className="payment-edit-input"
            placeholder="#ffffff"
          />
        </div>

        <div className="payment-edit-field">
          <label>Text Color:</label>
          <input
            type="color"
            value={styles.textColor}
            onChange={(e) => handleButtonStyleChange(buttonType, 'textColor', e.target.value)}
            className="payment-color-input"
          />
          <input
            type="text"
            value={styles.textColor}
            onChange={(e) => handleButtonStyleChange(buttonType, 'textColor', e.target.value)}
            className="payment-edit-input"
            placeholder="#000000"
          />
        </div>

        <div className="payment-edit-field">
          <label>Corner Radius:</label>
          <input
            type="text"
            value={styles.cornerRadius}
            onChange={(e) => handleButtonStyleChange(buttonType, 'cornerRadius', e.target.value)}
            className="payment-edit-input"
            placeholder="23px"
          />
        </div>
      </div>
    );
  }

  // Image upload renderer
  
  function renderImageUpload() {
    return (
      <div style={{ textAlign: 'center' }}>
        <div
          className="upload-btn-wrapper"
          onClick={handleImageClick}
        >
          <button className="btn">{imageUrl ? 'Change Image' : 'Upload Image'}</button>
          <input
  type="file"
  accept="image/*,.svg"   
  onChange={handleImageChange}
  ref={imageRef}
  style={{ display: 'none' }}
/>

        </div>
        <br />
        <span>PNG, JPG, GIF, or SVG (Max {MAX_IMAGE_SIZE_KB}KB)</span>

      </div>
    );
  }
  
  // preview renderer
  const renderPreview = () => (
    <div
      className={`payment-card-preview ${content.theme}`}
      style={{ background: content.backgroundColor }}
    >
      {/* Image Section */}
      {imageUrl && (
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <img
            src={imageUrl}
            alt="Service Icon"
            style={{ maxWidth: '100px', height: 'auto', display: 'block', margin: '0 auto' }}
          />
        </div>
      )}
      
      <p className="payment-title" style={{ color: content.titleColor }}>
        {content.textContent}
      </p>
      <h2 className="payment-amount" style={{ color: content.amountColor }}>
        $70.00
      </h2>
      <p className="payment-due" style={{ color: content.descriptionColor }}>
        {content.descriptionContent}
      </p>

      <div className="payment-buttons">
        <button
          className="payment-btn-primary"
          style={{
            backgroundColor: buttonStyles.backgroundColor,
            color: buttonStyles.textColor,
            borderRadius: buttonStyles.cornerRadius,
          }}
        >
          {content.buttonLabel}
        </button>
      </div>

      <div className="payment-buttons" style={{ marginTop: '8px' }}>
        <button
          className="payment-btn-secondary"
          style={{
            backgroundColor: buttonStyles2.backgroundColor,
            color: buttonStyles2.textColor,
            borderRadius: buttonStyles2.cornerRadius,
          }}
        >
          {content.secondaryButtonLabel}
        </button>
        {/* <button
          className="payment-btn-secondary"
          style={{
            backgroundColor: buttonStyles3.backgroundColor,
            color: buttonStyles3.textColor,
            borderRadius: buttonStyles3.cornerRadius,
          }}
        >
          {content.tertiaryButtonLabel}
        </button> */}
      </div>
    </div>
  );

  return (
    <div className="payment-editable-container">
      <Snackbar
        open={snackbar.open}
        autoHideDuration={SNACKBAR_DURATION}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {isLoading && <div className="payment-save-indicator">Saving...</div>}

      <div style={{ textAlign: 'right', marginBottom: '10px' }}>
        <button
          className="payment-editable-btn"
          onClick={() => setPreviewMode(!previewMode)}
        >
          {previewMode ? 'Back to Edit' : 'Preview'}
        </button>
      </div>

      {previewMode ? (
        renderPreview()
      ) : (
        <>
          {/* Image Section */}
          <div className="editable-image">
            {imageUrl && (
              <img
                src={imageUrl}
                alt="Service Icon"
              />
            )}
            <button
              type="button"
              className={`payment-editable-edit-btn ${editingSegment === 'image' ? 'act' : ''}`}
              onClick={() => handleEditClick('image')}
            />
            {editingSegment === 'image' && renderImageUpload()}
          </div>

          {/* Text Section */}
          <div className="payment-editable-text">
            {editingSegment === 'description' ? (
              <>
                <div className="input-wrapper">
                  <input
                    type="text"
                    className="payment-text-input"
                    value={content.textContent}
                    maxLength={50}
                    onChange={(e) => handleContentChange('textContent', e.target.value)}
                  />
                  <small className="char-counter">
                    {content.textContent.length}/50
                  </small>
                </div>

                <div className="input-wrapper">
                  <textarea
                    className="payment-textarea-input"
                    value={content.descriptionContent}
                    maxLength={200}
                    onChange={(e) => handleContentChange('descriptionContent', e.target.value)}
                    rows="4"
                  />
                  <small className="char-counter">
                    {content.descriptionContent.length}/200
                  </small>
                </div>
               
                {/* Background Color */}
                <div className="payment-edit-field">
                  <label>Card Background Color:</label>
                  <input
                    type="color"
                    value={content.backgroundColor}
                    onChange={(e) => handleContentChange('backgroundColor', e.target.value)}
                    className="payment-color-input"
                  />
                  <input
                    type="text"
                    value={content.backgroundColor}
                    onChange={(e) => handleContentChange('backgroundColor', e.target.value)}
                    className="payment-edit-input"
                    placeholder="#ffffff"
                  />
                </div>

                {/* Title Color */}
                <div className="payment-edit-field">
                  <label>Title Color:</label>
                  <input
                    type="color"
                    value={content.titleColor}
                    onChange={(e) => handleContentChange('titleColor', e.target.value)}
                    className="payment-color-input"
                  />
                  <input
                    type="text"
                    value={content.titleColor}
                    onChange={(e) => handleContentChange('titleColor', e.target.value)}
                    className="payment-edit-input"
                    placeholder="#000000"
                  />
                </div>

                {/* Description Color */}
                <div className="payment-edit-field">
                  <label>Description Color:</label>
                  <input
                    type="color"
                    value={content.descriptionColor}
                    onChange={(e) => handleContentChange('descriptionColor', e.target.value)}
                    className="payment-color-input"
                  />
                  <input
                    type="text"
                    value={content.descriptionColor}
                    onChange={(e) => handleContentChange('descriptionColor', e.target.value)}
                    className="payment-edit-input"
                    placeholder="#000000"
                  />
                </div>

                {/* Amount Color */}
                <div className="payment-edit-field">
                  <label>Amount Color:</label>
                  <input
                    type="color"
                    value={content.amountColor}
                    onChange={(e) => handleContentChange('amountColor', e.target.value)}
                    className="payment-color-input"
                  />
                  <input
                    type="text"
                    value={content.amountColor}
                    onChange={(e) => handleContentChange('amountColor', e.target.value)}
                    className="payment-edit-input"
                    placeholder="#000000"
                  />
                </div>
              </>
            ) : (
              <>
                <h2 style={{ color: content.titleColor }}>{content.textContent}</h2>
                <p style={{ color: content.descriptionColor }}>{content.descriptionContent}</p>
              </>
            )}

            <button
              type="button"
              className={`payment-editable-edit-btn ${editingSegment === 'description' ? 'active' : ''}`}
              onClick={() => handleEditClick('description')}
            />
          </div>

          {/* Primary button */}
          <div className="payment-editable-button">
            <button
              type="button"
              className="payment-primary-button"
              style={{
                backgroundColor: buttonStyles.backgroundColor,
                color: buttonStyles.textColor,
                borderRadius: buttonStyles.cornerRadius,
              }}
            >
              {content.buttonLabel}
            </button>

            <button
              type="button"
              className={`payment-editable-edit-btn ${editingSegment === 'button' ? 'active' : ''}`}
              onClick={() => handleEditClick('button')}
            />
            {editingSegment === 'button' && renderButtonEditModal('primary', buttonStyles)}
          </div>

          {/* Footer */}

          <div className="payment-editable-button">
  <button
    type="button"
    className="payment-secondary-button"
    style={{
      backgroundColor: buttonStyles2.backgroundColor,
      color: buttonStyles2.textColor,
      borderRadius: buttonStyles2.cornerRadius,
    }}
  >
    {content.secondaryButtonLabel}
  </button>

  <button
    type="button"
    className={`payment-editable-edit-btn ${editingSegment === 'button-2' ? 'active' : ''}`}
    onClick={() => handleEditClick('button-2')}
  />
  {editingSegment === 'button-2' && renderButtonEditModal('secondary', buttonStyles2)}
</div>

          <div className="payment-editable-footer" style={{display:'none'}}>
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                className="payment-secondary-button"
                style={{
                  backgroundColor: buttonStyles2.backgroundColor,
                  color: buttonStyles2.textColor,
                  borderRadius: buttonStyles2.cornerRadius,
                }}
              >
                {content.secondaryButtonLabel}
              </button>

              <button
                type="button"
                className={`payment-editable-edit-btn ${editingSegment === 'button-2' ? 'active' : ''}`}
                onClick={() => handleEditClick('button-2')}
                style={{ position: 'absolute', top: '4px', right: '4px' }}
              />
              {editingSegment === 'button-2' && renderButtonEditModal('secondary', buttonStyles2)}
            </div>

            {/* <div style={{ position: 'relative' }}>
              <button
                type="button"
                className="payment-secondary-button"
                style={{
                  backgroundColor: buttonStyles3.backgroundColor,
                  color: buttonStyles3.textColor,
                  borderRadius: buttonStyles3.cornerRadius,
                }}
              >
                {content.tertiaryButtonLabel}
              </button>

              <button
                type="button"
                className={`payment-editable-edit-btn ${editingSegment === 'button-3' ? 'active' : ''}`}
                onClick={() => handleEditClick('button-3')}
                style={{ position: 'absolute', top: '4px', right: '4px' }}
              />
              {editingSegment === 'button-3' && renderButtonEditModal('tertiary', buttonStyles3)}
            </div> */}
          </div>

          {/* Save Section */}
          <div className="payment-editable-save">
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                className="payment-editable-save-btn"
                onClick={() => saveTemplate()}
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : 'Save Template'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TemporaryDisconnectBase;

